import { useEffect, useState } from 'react'
import {
    PINNED_PARTICIPATION_PARAMETERS_VERSION,
    participationParametersUrl,
} from '../helpers/participationHelpers/participationParametersVersion'

// Live source of the participatieladder monetarisation parameters. Replaces the
// bundled defaultParticipationParameters: the figures (CBS income, tax brackets,
// benefit costs) come from the Meetstandaard API so they can be updated
// without a frontend release.
//
// VASTGEZET op PINNED_PARTICIPATION_PARAMETERS_VERSION (bug #118). Een nieuwe
// set in de API verandert hier niets tot die constante omgaat; een antwoord of
// cache met een andere versie is een fout, geen stille terugval.
const PARAMETERS_URL = participationParametersUrl(PINNED_PARTICIPATION_PARAMETERS_VERSION)

const CACHE_KEY = `participationParametersCache:${PINNED_PARTICIPATION_PARAMETERS_VERSION}`
const WEEK_MS = 7 * 24 * 60 * 60 * 1000

/**
 * @typedef {object} ParticipationParameters
 * @property {{ version: string, taxYear?: number, source?: string, updatedAt?: string }} meta
 * @property {number} weeksPerYear
 * @property {number} volunteerValuePerHour
 * @property {Array<{ upTo: number, rate: number }>} taxBrackets - `upTo` is Infinity for the open top bracket
 * @property {Object<string, Object<string, number>>} incomeByEducationAndHoursBand
 * @property {Object<string, number>} benefitAmountPerYear
 * @property {number} [bijstandUitvoeringskostenPerYear]
 */

/**
 * The API sends the open top tax bracket as `{ upTo: null }`. computeIncomeTax
 * relies on `Math.min(income, upTo)`, where `null` would collapse to 0 and break
 * the calculation, so map `null` -> Infinity. Done on read (never stored, since
 * JSON cannot serialise Infinity).
 * @param {object} raw - the raw API payload
 * @returns {ParticipationParameters} the adapted parameters
 */
const adaptParameters = (raw) => {
    if (!raw || !Array.isArray(raw.taxBrackets)) return raw
    return {
        ...raw,
        taxBrackets: raw.taxBrackets.map((bracket) => ({
            ...bracket,
            upTo: bracket.upTo === null ? Infinity : bracket.upTo,
        })),
    }
}

const loadCached = () => {
    try {
        const raw = localStorage.getItem(CACHE_KEY)
        if (!raw) return null
        return JSON.parse(raw)
    } catch (e) {
        console.warn('Failed to parse participation parameters cache', e)
        return null
    }
}

const saveCached = (data) => {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data, fetchedAt: Date.now() }))
    } catch (e) {
        console.warn('Failed to save participation parameters cache', e)
    }
}

/**
 * Fetches the participatieladder model parameters from the Meetstandaard API,
 * caching the payload in localStorage for a week. On failure it surfaces the
 * error and returns no parameters (fail visibly — never falls back to bundled
 * defaults, so callers must skip computation when `parameters` is null).
 *
 * @returns {{ parameters: ParticipationParameters | null, loading: boolean, error: Error | null }}
 */
const useParticipationParameters = () => {
    const [parameters, setParameters] = useState(/** @type {ParticipationParameters | null} */ (null))
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(/** @type {Error | null} */ (null))

    useEffect(() => {
        const cached = loadCached()
        const now = Date.now()

        // Serve a fresh cache (< 1 week old) immediately. A cache predating the
        // sourceRefs/bronnen fields is treated as stale so the bronnen modals
        // get real data instead of falling back to "geen bron beschikbaar".
        const hasSources = (data) => data && data.sourceRefs && data.bronnen
        const isPinned = (data) => data?.meta?.version === PINNED_PARTICIPATION_PARAMETERS_VERSION
        if (cached && now - cached.fetchedAt < WEEK_MS && hasSources(cached.data) && isPinned(cached.data)) {
            setParameters(adaptParameters(cached.data))
            setLoading(false)
            return
        }

        let cancelled = false

        const fetchParameters = async () => {
            try {
                const res = await fetch(PARAMETERS_URL)
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                const json = await res.json()
                if (cancelled) return
                if (!isPinned(json)) {
                    throw new Error(`Parameterversie ${json?.meta?.version} ontvangen, ${PINNED_PARTICIPATION_PARAMETERS_VERSION} verwacht`)
                }

                setParameters(adaptParameters(json))
                setError(null)
                setLoading(false)
                saveCached(json)
            } catch (err) {
                console.error('Error fetching participation parameters', err)
                if (cancelled) return

                setError(err)
                setParameters(null)
                setLoading(false)
            }
        }

        fetchParameters()

        return () => {
            cancelled = true
        }
    }, [])

    return { parameters, loading, error }
}

export default useParticipationParameters
